function getFocusableElements(container) {
  return Array.from(
    container.querySelectorAll(
      "summary, a[href], button:enabled, [tabindex]:not([tabindex^='-']), [draggable], area, input:not([type=hidden]):enabled, select:enabled, textarea:enabled, object, iframe"
    )
  );
}

class SectionId {
  static #separator = '__';

  // for a qualified section id (e.g. 'template--22224696705326__main'), return just the section id (e.g. 'template--22224696705326')
  static parseId(qualifiedSectionId) {
    return qualifiedSectionId.split(SectionId.#separator)[0];
  }

  // for a qualified section id (e.g. 'template--22224696705326__main'), return just the section name (e.g. 'main')
  static parseSectionName(qualifiedSectionId) {
    return qualifiedSectionId.split(SectionId.#separator)[1];
  }

  // for a section id (e.g. 'template--22224696705326') and a section name (e.g. 'recommended-products'), return a qualified section id (e.g. 'template--22224696705326__recommended-products')
  static getIdForSection(sectionId, sectionName) {
    return `${sectionId}${SectionId.#separator}${sectionName}`;
  }
}

class HTMLUpdateUtility {
  /**
   * Used to swap an HTML node with a new node.
   * The new node is inserted as a previous sibling to the old node, the old node is hidden, and then the old node is removed.
   *
   * The function currently uses a double buffer approach, but this should be replaced by a view transition once it is more widely supported https://developer.mozilla.org/en-US/docs/Web/API/View_Transitions_API
   */
  static viewTransition(oldNode, newContent, preProcessCallbacks = [], postProcessCallbacks = []) {
    preProcessCallbacks?.forEach((callback) => callback(newContent));

    const newNodeWrapper = document.createElement('div');
    HTMLUpdateUtility.setInnerHTML(newNodeWrapper, newContent.outerHTML);
    const newNode = newNodeWrapper.firstChild;

    // dedupe IDs
    const uniqueKey = Date.now();
    oldNode.querySelectorAll('[id], [form]').forEach((element) => {
      element.id && (element.id = `${element.id}-${uniqueKey}`);
      element.form && element.setAttribute('form', `${element.form.getAttribute('id')}-${uniqueKey}`);
    });

    oldNode.parentNode.insertBefore(newNode, oldNode);
    oldNode.style.display = 'none';

    postProcessCallbacks?.forEach((callback) => callback(newNode));

    setTimeout(() => oldNode.remove(), 500);
  }

  // Sets inner HTML and reinjects the script tags to allow execution. By default, scripts are disabled when using element.innerHTML.
  static setInnerHTML(element, html) {
    element.innerHTML = html;
    element.querySelectorAll('script').forEach((oldScriptTag) => {
      const newScriptTag = document.createElement('script');
      Array.from(oldScriptTag.attributes).forEach((attribute) => {
        newScriptTag.setAttribute(attribute.name, attribute.value);
      });
      newScriptTag.appendChild(document.createTextNode(oldScriptTag.innerHTML));
      oldScriptTag.parentNode.replaceChild(newScriptTag, oldScriptTag);
    });
  }
}

document.querySelectorAll('[id^="Details-"] summary').forEach((summary) => {
  summary.setAttribute('role', 'button');
  summary.setAttribute('aria-expanded', summary.parentNode.hasAttribute('open'));

  if (summary.nextElementSibling.getAttribute('id')) {
    summary.setAttribute('aria-controls', summary.nextElementSibling.id);
  }

  summary.addEventListener('click', (event) => {
    event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
  });

  if (summary.closest('header-drawer, menu-drawer')) return;
  summary.parentElement.addEventListener('keyup', onKeyUpEscape);
});

const trapFocusHandlers = {};

function trapFocus(container, elementToFocus = container) {
  var elements = getFocusableElements(container);
  var first = elements[0];
  var last = elements[elements.length - 1];

  removeTrapFocus();

  trapFocusHandlers.focusin = (event) => {
    if (event.target !== container && event.target !== last && event.target !== first) return;

    document.addEventListener('keydown', trapFocusHandlers.keydown);
  };

  trapFocusHandlers.focusout = function () {
    document.removeEventListener('keydown', trapFocusHandlers.keydown);
  };

  trapFocusHandlers.keydown = function (event) {
    if (event.code.toUpperCase() !== 'TAB') return; // If not TAB key
    // On the last focusable element and tab forward, focus the first element.
    if (event.target === last && !event.shiftKey) {
      event.preventDefault();
      first.focus();
    }

    //  On the first focusable element and tab backward, focus the last element.
    if ((event.target === container || event.target === first) && event.shiftKey) {
      event.preventDefault();
      last.focus();
    }
  };

  document.addEventListener('focusout', trapFocusHandlers.focusout);
  document.addEventListener('focusin', trapFocusHandlers.focusin);

  elementToFocus.focus();

  if (
    elementToFocus.tagName === 'INPUT' &&
    ['search', 'text', 'email', 'url'].includes(elementToFocus.type) &&
    elementToFocus.value
  ) {
    elementToFocus.setSelectionRange(0, elementToFocus.value.length);
  }
}

// Here run the querySelector to figure out if the browser supports :focus-visible or not and run code based on it.
try {
  document.querySelector(':focus-visible');
} catch (e) {
  focusVisiblePolyfill();
}

function focusVisiblePolyfill() {
  const navKeys = [
    'ARROWUP',
    'ARROWDOWN',
    'ARROWLEFT',
    'ARROWRIGHT',
    'TAB',
    'ENTER',
    'SPACE',
    'ESCAPE',
    'HOME',
    'END',
    'PAGEUP',
    'PAGEDOWN',
  ];
  let currentFocusedElement = null;
  let mouseClick = null;

  window.addEventListener('keydown', (event) => {
    if (navKeys.includes(event.code.toUpperCase())) {
      mouseClick = false;
    }
  });

  window.addEventListener('mousedown', (event) => {
    mouseClick = true;
  });

  window.addEventListener(
    'focus',
    () => {
      if (currentFocusedElement) currentFocusedElement.classList.remove('focused');

      if (mouseClick) return;

      currentFocusedElement = document.activeElement;
      currentFocusedElement.classList.add('focused');
    },
    true
  );
}

// Keep programmatic focus restoration accessible without drawing a ring after a tap.
// Safari can carry :focus-visible from a dialog back to its pointer-activated opener.
(() => {
  const setPointer = () => { document.documentElement.dataset.saInput = 'pointer'; };
  document.addEventListener('pointerdown', setPointer, true);
  document.addEventListener('touchstart', setPointer, {capture: true, passive: true});
  document.addEventListener('mousedown', setPointer, true);
  document.addEventListener('keydown', (event) => {
    if (['Shift', 'Control', 'Alt', 'Meta'].includes(event.key)) return;
    document.documentElement.dataset.saInput = 'keyboard';
  }, true);
})();

function pauseAllMedia() {
  document.querySelectorAll('.js-youtube').forEach((video) => {
    video.contentWindow.postMessage('{"event":"command","func":"' + 'pauseVideo' + '","args":""}', '*');
  });
  document.querySelectorAll('.js-vimeo').forEach((video) => {
    video.contentWindow.postMessage('{"method":"pause"}', '*');
  });
  document.querySelectorAll('video').forEach((video) => video.pause());
  document.querySelectorAll('product-model').forEach((model) => {
    if (model.modelViewerUI) model.modelViewerUI.pause();
  });
}

function removeTrapFocus(elementToFocus = null) {
  document.removeEventListener('focusin', trapFocusHandlers.focusin);
  document.removeEventListener('focusout', trapFocusHandlers.focusout);
  document.removeEventListener('keydown', trapFocusHandlers.keydown);

  if (elementToFocus) elementToFocus.focus();
}

function onKeyUpEscape(event) {
  if (event.code.toUpperCase() !== 'ESCAPE') return;

  const openDetailsElement = event.target.closest('details[open]');
  if (!openDetailsElement) return;

  const summaryElement = openDetailsElement.querySelector('summary');
  openDetailsElement.removeAttribute('open');
  summaryElement.setAttribute('aria-expanded', false);
  summaryElement.focus();
}

class QuantityInput extends HTMLElement {
  constructor() {
    super();
    this.input = this.querySelector('input');
    this.changeEvent = new Event('change', { bubbles: true });
    this.input.addEventListener('change', this.onInputChange.bind(this));
    this.querySelectorAll('button').forEach((button) =>
      button.addEventListener('click', this.onButtonClick.bind(this))
    );
  }

  quantityUpdateUnsubscriber = undefined;

  connectedCallback() {
    this.validateQtyRules();
    this.quantityUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.quantityUpdate, this.validateQtyRules.bind(this));
  }

  disconnectedCallback() {
    if (this.quantityUpdateUnsubscriber) {
      this.quantityUpdateUnsubscriber();
    }
  }

  onInputChange(event) {
    this.validateQtyRules();
  }

  onButtonClick(event) {
    event.preventDefault();
    const previousValue = this.input.value;

    if (event.target.name === 'plus') {
      if (parseInt(this.input.dataset.min) > parseInt(this.input.step) && this.input.value == 0) {
        this.input.value = this.input.dataset.min;
      } else {
        this.input.stepUp();
      }
    } else {
      this.input.stepDown();
    }

    if (previousValue !== this.input.value) this.input.dispatchEvent(this.changeEvent);

    if (this.input.dataset.min === previousValue && event.target.name === 'minus') {
      this.input.value = parseInt(this.input.min);
    }
  }

  validateQtyRules() {
    const value = parseInt(this.input.value);
    if (this.input.min) {
      const buttonMinus = this.querySelector(".quantity__button[name='minus']");
      buttonMinus.classList.toggle('disabled', parseInt(value) <= parseInt(this.input.min));
    }
    if (this.input.max) {
      const max = parseInt(this.input.max);
      const buttonPlus = this.querySelector(".quantity__button[name='plus']");
      buttonPlus.classList.toggle('disabled', value >= max);
    }
  }
}

customElements.define('quantity-input', QuantityInput);

function debounce(fn, wait) {
  let t;
  return (...args) => {
    clearTimeout(t);
    t = setTimeout(() => fn.apply(this, args), wait);
  };
}


function throttle(fn, delay) {
  let lastCall = 0;
  return function (...args) {
    const now = new Date().getTime();
    if (now - lastCall < delay) {
      return;
    }
    lastCall = now;
    return fn(...args);
  };
}

function fetchConfig(type = 'json') {
  return {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: `application/${type}` },
  };
}

/*
 * Shopify Common JS
 *
 */
if (typeof window.Shopify == 'undefined') {
  window.Shopify = {};
}

Shopify.bind = function (fn, scope) {
  return function () {
    return fn.apply(scope, arguments);
  };
};

Shopify.setSelectorByValue = function (selector, value) {
  for (var i = 0, count = selector.options.length; i < count; i++) {
    var option = selector.options[i];
    if (value == option.value || value == option.innerHTML) {
      selector.selectedIndex = i;
      return i;
    }
  }
};

Shopify.addListener = function (target, eventName, callback) {
  target.addEventListener
    ? target.addEventListener(eventName, callback, false)
    : target.attachEvent('on' + eventName, callback);
};

Shopify.postLink = function (path, options) {
  options = options || {};
  var method = options['method'] || 'post';
  var params = options['parameters'] || {};

  var form = document.createElement('form');
  form.setAttribute('method', method);
  form.setAttribute('action', path);

  for (var key in params) {
    var hiddenField = document.createElement('input');
    hiddenField.setAttribute('type', 'hidden');
    hiddenField.setAttribute('name', key);
    hiddenField.setAttribute('value', params[key]);
    form.appendChild(hiddenField);
  }
  document.body.appendChild(form);
  form.submit();
  document.body.removeChild(form);
};

Shopify.CountryProvinceSelector = function (country_domid, province_domid, options) {
  this.countryEl = document.getElementById(country_domid);
  this.provinceEl = document.getElementById(province_domid);
  this.provinceContainer = document.getElementById(options['hideElement'] || province_domid);

  Shopify.addListener(this.countryEl, 'change', Shopify.bind(this.countryHandler, this));

  this.initCountry();
  this.initProvince();
};

Shopify.CountryProvinceSelector.prototype = {
  initCountry: function () {
    var value = this.countryEl.getAttribute('data-default');
    Shopify.setSelectorByValue(this.countryEl, value);
    this.countryHandler();
  },

  initProvince: function () {
    var value = this.provinceEl.getAttribute('data-default');
    if (value && this.provinceEl.options.length > 0) {
      Shopify.setSelectorByValue(this.provinceEl, value);
    }
  },

  countryHandler: function (e) {
    var opt = this.countryEl.options[this.countryEl.selectedIndex];
    var raw = opt.getAttribute('data-provinces');
    var provinces = JSON.parse(raw);

    this.clearOptions(this.provinceEl);
    if (provinces && provinces.length == 0) {
      this.provinceContainer.style.display = 'none';
    } else {
      for (var i = 0; i < provinces.length; i++) {
        var opt = document.createElement('option');
        opt.value = provinces[i][0];
        opt.innerHTML = provinces[i][1];
        this.provinceEl.appendChild(opt);
      }

      this.provinceContainer.style.display = '';
    }
  },

  clearOptions: function (selector) {
    while (selector.firstChild) {
      selector.removeChild(selector.firstChild);
    }
  },

  setOptions: function (selector, values) {
    for (var i = 0, count = values.length; i < values.length; i++) {
      var opt = document.createElement('option');
      opt.value = values[i];
      opt.innerHTML = values[i];
      selector.appendChild(opt);
    }
  },
};

class MenuDrawer extends HTMLElement {
  constructor() {
    super();

    this.mainDetailsToggle = this.querySelector('details');

    this.addEventListener('keyup', this.onKeyUp.bind(this));
    this.addEventListener('focusout', this.onFocusOut.bind(this));
    this.bindEvents();
  }

  bindEvents() {
    this.querySelectorAll('summary').forEach((summary) =>
      summary.addEventListener('click', this.onSummaryClick.bind(this))
    );
    this.querySelectorAll(
      'button:not(.localization-selector):not(.country-selector__close-button):not(.country-filter__reset-button)'
    ).forEach((button) => button.addEventListener('click', this.onCloseButtonClick.bind(this)));
  }

  onKeyUp(event) {
    if (event.code.toUpperCase() !== 'ESCAPE') return;

    const openDetailsElement = event.target.closest('details[open]');
    if (!openDetailsElement) return;

    openDetailsElement === this.mainDetailsToggle
      ? this.closeMenuDrawer(event, this.mainDetailsToggle.querySelector('summary'))
      : this.closeSubmenu(openDetailsElement);
  }

  onSummaryClick(event) {
    const summaryElement = event.currentTarget;
    const detailsElement = summaryElement.parentNode;
    const parentMenuElement = detailsElement.closest('.has-submenu');
    const isOpen = detailsElement.hasAttribute('open');
    const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

    function addTrapFocus() {
      trapFocus(summaryElement.nextElementSibling, detailsElement.querySelector('button'));
      summaryElement.nextElementSibling.removeEventListener('transitionend', addTrapFocus);
    }

    if (detailsElement === this.mainDetailsToggle) {
      if (isOpen) event.preventDefault();
      isOpen ? this.closeMenuDrawer(event, summaryElement) : this.openMenuDrawer(summaryElement);

      if (window.matchMedia('(max-width: 990px)')) {
        document.documentElement.style.setProperty('--viewport-height', `${window.innerHeight}px`);
      }
    } else {
      setTimeout(() => {
        detailsElement.classList.add('menu-opening');
        summaryElement.setAttribute('aria-expanded', true);
        parentMenuElement && parentMenuElement.classList.add('submenu-open');
        !reducedMotion || reducedMotion.matches
          ? addTrapFocus()
          : summaryElement.nextElementSibling.addEventListener('transitionend', addTrapFocus);
      }, 100);
    }
  }

  openMenuDrawer(summaryElement) {
    setTimeout(() => {
      this.mainDetailsToggle.classList.add('menu-opening');
    });
    summaryElement.setAttribute('aria-expanded', true);
    trapFocus(this.mainDetailsToggle, summaryElement);
    document.body.classList.add(`overflow-hidden-${this.dataset.breakpoint}`);
  }

  closeMenuDrawer(event, elementToFocus = false) {
    if (event === undefined) return;

    this.mainDetailsToggle.classList.remove('menu-opening');
    this.mainDetailsToggle.querySelectorAll('details').forEach((details) => {
      details.removeAttribute('open');
      details.classList.remove('menu-opening');
    });
    this.mainDetailsToggle.querySelectorAll('.submenu-open').forEach((submenu) => {
      submenu.classList.remove('submenu-open');
    });
    document.body.classList.remove(`overflow-hidden-${this.dataset.breakpoint}`);
    removeTrapFocus(elementToFocus);
    this.closeAnimation(this.mainDetailsToggle);

    if (event instanceof KeyboardEvent) elementToFocus?.setAttribute('aria-expanded', false);
  }

  onFocusOut() {
    setTimeout(() => {
      if (this.mainDetailsToggle.hasAttribute('open') && !this.mainDetailsToggle.contains(document.activeElement))
        this.closeMenuDrawer();
    });
  }

  onCloseButtonClick(event) {
    const detailsElement = event.currentTarget.closest('details');
    this.closeSubmenu(detailsElement);
  }

  closeSubmenu(detailsElement) {
    const parentMenuElement = detailsElement.closest('.submenu-open');
    parentMenuElement && parentMenuElement.classList.remove('submenu-open');
    detailsElement.classList.remove('menu-opening');
    detailsElement.querySelector('summary').setAttribute('aria-expanded', false);
    removeTrapFocus(detailsElement.querySelector('summary'));
    this.closeAnimation(detailsElement);
  }

  closeAnimation(detailsElement) {
    let animationStart;

    const handleAnimation = (time) => {
      if (animationStart === undefined) {
        animationStart = time;
      }

      const elapsedTime = time - animationStart;

      if (elapsedTime < 400) {
        window.requestAnimationFrame(handleAnimation);
      } else {
        detailsElement.removeAttribute('open');
        if (detailsElement.closest('details[open]')) {
          trapFocus(detailsElement.closest('details[open]'), detailsElement.querySelector('summary'));
        }
      }
    };

    window.requestAnimationFrame(handleAnimation);
  }
}

customElements.define('menu-drawer', MenuDrawer);

class HeaderDrawer extends MenuDrawer {
  constructor() {
    super();
  }

  openMenuDrawer(summaryElement) {
    this.header = this.header || document.querySelector('.section-header');
    this.borderOffset =
      this.borderOffset || this.closest('.header-wrapper').classList.contains('header-wrapper--border-bottom') ? 1 : 0;
    document.documentElement.style.setProperty(
      '--header-bottom-position',
      `${parseInt(this.header.getBoundingClientRect().bottom - this.borderOffset)}px`
    );
    this.header.classList.add('menu-open');

    setTimeout(() => {
      this.mainDetailsToggle.classList.add('menu-opening');
    });

    summaryElement.setAttribute('aria-expanded', true);
    window.addEventListener('resize', this.onResize);
    trapFocus(this.mainDetailsToggle, summaryElement);
    document.body.classList.add(`overflow-hidden-${this.dataset.breakpoint}`);
  }

  closeMenuDrawer(event, elementToFocus) {
    if (!elementToFocus) return;
    super.closeMenuDrawer(event, elementToFocus);
    this.header.classList.remove('menu-open');
    window.removeEventListener('resize', this.onResize);
  }

  onResize = () => {
    this.header &&
      document.documentElement.style.setProperty(
        '--header-bottom-position',
        `${parseInt(this.header.getBoundingClientRect().bottom - this.borderOffset)}px`
      );
    document.documentElement.style.setProperty('--viewport-height', `${window.innerHeight}px`);
  };
}

customElements.define('header-drawer', HeaderDrawer);

class ModalDialog extends HTMLElement {
  constructor() {
    super();
    this.querySelector('[id^="ModalClose-"]').addEventListener('click', this.hide.bind(this, false));
    this.addEventListener('keyup', (event) => {
      if (event.code.toUpperCase() === 'ESCAPE') this.hide();
    });
    if (this.classList.contains('media-modal')) {
      this.addEventListener('pointerup', (event) => {
        if (event.pointerType === 'mouse' && !event.target.closest('deferred-media, product-model')) this.hide();
      });
    } else {
      this.addEventListener('click', (event) => {
        if (event.target === this) this.hide();
      });
    }
  }

  connectedCallback() {
    if (this.moved) return;
    this.moved = true;
    this.dataset.section = this.closest('.shopify-section').id.replace('shopify-section-', '');
    document.body.appendChild(this);
  }

  show(opener) {
    this.openedBy = opener;
    const popup = this.querySelector('.template-popup');
    document.body.classList.add('overflow-hidden');
    this.setAttribute('open', '');
    if (popup) popup.loadContent();
    trapFocus(this, this.querySelector('[role="dialog"]'));
    window.pauseAllMedia();
  }

  hide() {
    document.body.classList.remove('overflow-hidden');
    document.body.dispatchEvent(new CustomEvent('modalClosed'));
    this.removeAttribute('open');
    removeTrapFocus(this.openedBy);
    window.pauseAllMedia();
  }
}
customElements.define('modal-dialog', ModalDialog);

class BulkModal extends HTMLElement {
  constructor() {
    super();
  }

  connectedCallback() {
    const handleIntersection = (entries, observer) => {
      if (!entries[0].isIntersecting) return;
      observer.unobserve(this);
      if (this.innerHTML.trim() === '') {
        const productUrl = this.dataset.url.split('?')[0];
        fetch(`${productUrl}?section_id=bulk-quick-order-list`)
          .then((response) => response.text())
          .then((responseText) => {
            const html = new DOMParser().parseFromString(responseText, 'text/html');
            const sourceQty = html.querySelector('.quick-order-list-container').parentNode;
            this.innerHTML = sourceQty.innerHTML;
          })
          .catch((e) => {
            console.error(e);
          });
      }
    };

    new IntersectionObserver(handleIntersection.bind(this)).observe(
      document.querySelector(`#QuickBulk-${this.dataset.productId}-${this.dataset.sectionId}`)
    );
  }
}

customElements.define('bulk-modal', BulkModal);

class ModalOpener extends HTMLElement {
  constructor() {
    super();

    const button = this.querySelector('button');

    if (!button) return;
    button.addEventListener('click', () => {
      const modal = document.querySelector(this.getAttribute('data-modal'));
      if (modal) modal.show(button);
    });
  }
}
customElements.define('modal-opener', ModalOpener);

class DeferredMedia extends HTMLElement {
  constructor() {
    super();
    const poster = this.querySelector('[id^="Deferred-Poster-"]');
    if (!poster) return;
    poster.addEventListener('click', this.loadContent.bind(this));
  }

  loadContent(focus = true) {
    window.pauseAllMedia();
    if (!this.getAttribute('loaded')) {
      const content = document.createElement('div');
      content.appendChild(this.querySelector('template').content.firstElementChild.cloneNode(true));

      this.setAttribute('loaded', true);
      const deferredElement = this.appendChild(content.querySelector('video, model-viewer, iframe'));
      if (focus) deferredElement.focus();
      if (deferredElement.nodeName == 'VIDEO' && deferredElement.getAttribute('autoplay')) {
        // force autoplay for safari
        deferredElement.play();
      }

      // Workaround for safari iframe bug
      const formerStyle = deferredElement.getAttribute('style');
      deferredElement.setAttribute('style', 'display: block;');
      window.setTimeout(() => {
        deferredElement.setAttribute('style', formerStyle);
      }, 0);
    }
  }
}

customElements.define('deferred-media', DeferredMedia);

class SliderComponent extends HTMLElement {
  constructor() {
    super();
    this.slider = this.querySelector('[id^="Slider-"]');
    this.sliderItems = this.querySelectorAll('[id^="Slide-"]');
    this.enableSliderLooping = false;
    this.currentPageElement = this.querySelector('.slider-counter--current');
    this.pageTotalElement = this.querySelector('.slider-counter--total');
    this.prevButton = this.querySelector('button[name="previous"]');
    this.nextButton = this.querySelector('button[name="next"]');

    if (!this.slider || !this.nextButton) return;

    this.initPages();
    const resizeObserver = new ResizeObserver((entries) => this.initPages());
    resizeObserver.observe(this.slider);

    this.slider.addEventListener('scroll', this.update.bind(this));
    this.prevButton.addEventListener('click', this.onButtonClick.bind(this));
    this.nextButton.addEventListener('click', this.onButtonClick.bind(this));
  }

  initPages() {
    this.sliderItemsToShow = Array.from(this.sliderItems).filter((element) => element.clientWidth > 0);
    if (this.sliderItemsToShow.length < 2) return;
    this.sliderItemOffset = this.sliderItemsToShow[1].offsetLeft - this.sliderItemsToShow[0].offsetLeft;
    this.slidesPerPage = Math.floor(
      (this.slider.clientWidth - this.sliderItemsToShow[0].offsetLeft) / this.sliderItemOffset
    );
    this.totalPages = this.sliderItemsToShow.length - this.slidesPerPage + 1;
    this.update();
  }

  resetPages() {
    this.sliderItems = this.querySelectorAll('[id^="Slide-"]');
    this.initPages();
  }

  update() {
    // Temporarily prevents unneeded updates resulting from variant changes
    // This should be refactored as part of https://github.com/Shopify/dawn/issues/2057
    if (!this.slider || !this.nextButton) return;

    const previousPage = this.currentPage;
    this.currentPage = Math.round(this.slider.scrollLeft / this.sliderItemOffset) + 1;

    if (this.currentPageElement && this.pageTotalElement) {
      this.currentPageElement.textContent = this.currentPage;
      this.pageTotalElement.textContent = this.totalPages;
    }

    if (this.currentPage != previousPage) {
      this.dispatchEvent(
        new CustomEvent('slideChanged', {
          detail: {
            currentPage: this.currentPage,
            currentElement: this.sliderItemsToShow[this.currentPage - 1],
          },
        })
      );
    }

    if (this.enableSliderLooping) return;

    if (this.isSlideVisible(this.sliderItemsToShow[0]) && this.slider.scrollLeft === 0) {
      this.prevButton.setAttribute('disabled', 'disabled');
    } else {
      this.prevButton.removeAttribute('disabled');
    }

    if (this.isSlideVisible(this.sliderItemsToShow[this.sliderItemsToShow.length - 1])) {
      this.nextButton.setAttribute('disabled', 'disabled');
    } else {
      this.nextButton.removeAttribute('disabled');
    }
  }

  isSlideVisible(element, offset = 0) {
    const lastVisibleSlide = this.slider.clientWidth + this.slider.scrollLeft - offset;
    return element.offsetLeft + element.clientWidth <= lastVisibleSlide && element.offsetLeft >= this.slider.scrollLeft;
  }

  onButtonClick(event) {
    event.preventDefault();
    const step = event.currentTarget.dataset.step || 1;
    this.slideScrollPosition =
      event.currentTarget.name === 'next'
        ? this.slider.scrollLeft + step * this.sliderItemOffset
        : this.slider.scrollLeft - step * this.sliderItemOffset;
    this.setSlidePosition(this.slideScrollPosition);
  }

  setSlidePosition(position) {
    this.slider.scrollTo({
      left: position,
    });
  }
}

customElements.define('slider-component', SliderComponent);

class SlideshowComponent extends SliderComponent {
  constructor() {
    super();
    this.sliderControlWrapper = this.querySelector('.slider-buttons');
    this.enableSliderLooping = true;

    if (!this.sliderControlWrapper) return;

    this.sliderFirstItemNode = this.slider.querySelector('.slideshow__slide');
    if (this.sliderItemsToShow.length > 0) this.currentPage = 1;

    this.announcementBarSlider = this.querySelector('.announcement-bar-slider');
    // Value below should match --duration-announcement-bar CSS value
    this.announcerBarAnimationDelay = this.announcementBarSlider ? 250 : 0;

    this.sliderControlLinksArray = Array.from(this.sliderControlWrapper.querySelectorAll('.slider-counter__link'));
    this.sliderControlLinksArray.forEach((link) => link.addEventListener('click', this.linkToSlide.bind(this)));
    this.slider.addEventListener('scroll', this.setSlideVisibility.bind(this));
    this.setSlideVisibility();

    if (this.announcementBarSlider) {
      this.announcementBarArrowButtonWasClicked = false;

      this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
      this.reducedMotion.addEventListener('change', () => {
        if (this.slider.getAttribute('data-autoplay') === 'true') this.setAutoPlay();
      });

      [this.prevButton, this.nextButton].forEach((button) => {
        button.addEventListener(
          'click',
          () => {
            this.announcementBarArrowButtonWasClicked = true;
          },
          { once: true }
        );
      });
    }

    if (this.slider.getAttribute('data-autoplay') === 'true') this.setAutoPlay();
  }

  setAutoPlay() {
    this.autoplaySpeed = this.slider.dataset.speed * 1000;
    this.addEventListener('mouseover', this.focusInHandling.bind(this));
    this.addEventListener('mouseleave', this.focusOutHandling.bind(this));
    this.addEventListener('focusin', this.focusInHandling.bind(this));
    this.addEventListener('focusout', this.focusOutHandling.bind(this));

    if (this.querySelector('.slideshow__autoplay')) {
      this.sliderAutoplayButton = this.querySelector('.slideshow__autoplay');
      this.sliderAutoplayButton.addEventListener('click', this.autoPlayToggle.bind(this));
      this.autoplayButtonIsSetToPlay = true;
      this.play();
    } else {
      this.reducedMotion.matches || this.announcementBarArrowButtonWasClicked ? this.pause() : this.play();
    }
  }

  onButtonClick(event) {
    super.onButtonClick(event);
    this.wasClicked = true;

    const isFirstSlide = this.currentPage === 1;
    const isLastSlide = this.currentPage === this.sliderItemsToShow.length;

    if (!isFirstSlide && !isLastSlide) {
      this.applyAnimationToAnnouncementBar(event.currentTarget.name);
      return;
    }

    if (isFirstSlide && event.currentTarget.name === 'previous') {
      this.slideScrollPosition =
        this.slider.scrollLeft + this.sliderFirstItemNode.clientWidth * this.sliderItemsToShow.length;
    } else if (isLastSlide && event.currentTarget.name === 'next') {
      this.slideScrollPosition = 0;
    }

    this.setSlidePosition(this.slideScrollPosition);

    this.applyAnimationToAnnouncementBar(event.currentTarget.name);
  }

  setSlidePosition(position) {
    if (this.setPositionTimeout) clearTimeout(this.setPositionTimeout);
    this.setPositionTimeout = setTimeout(() => {
      this.slider.scrollTo({
        left: position,
      });
    }, this.announcerBarAnimationDelay);
  }

  update() {
    super.update();
    this.sliderControlButtons = this.querySelectorAll('.slider-counter__link');
    this.prevButton.removeAttribute('disabled');

    if (!this.sliderControlButtons.length) return;

    this.sliderControlButtons.forEach((link) => {
      link.classList.remove('slider-counter__link--active');
      link.removeAttribute('aria-current');
    });
    this.sliderControlButtons[this.currentPage - 1].classList.add('slider-counter__link--active');
    this.sliderControlButtons[this.currentPage - 1].setAttribute('aria-current', true);
  }

  autoPlayToggle() {
    this.togglePlayButtonState(this.autoplayButtonIsSetToPlay);
    this.autoplayButtonIsSetToPlay ? this.pause() : this.play();
    this.autoplayButtonIsSetToPlay = !this.autoplayButtonIsSetToPlay;
  }

  focusOutHandling(event) {
    if (this.sliderAutoplayButton) {
      const focusedOnAutoplayButton =
        event.target === this.sliderAutoplayButton || this.sliderAutoplayButton.contains(event.target);
      if (!this.autoplayButtonIsSetToPlay || focusedOnAutoplayButton) return;
      this.play();
    } else if (!this.reducedMotion.matches && !this.announcementBarArrowButtonWasClicked) {
      this.play();
    }
  }

  focusInHandling(event) {
    if (this.sliderAutoplayButton) {
      const focusedOnAutoplayButton =
        event.target === this.sliderAutoplayButton || this.sliderAutoplayButton.contains(event.target);
      if (focusedOnAutoplayButton && this.autoplayButtonIsSetToPlay) {
        this.play();
      } else if (this.autoplayButtonIsSetToPlay) {
        this.pause();
      }
    } else if (this.announcementBarSlider.contains(event.target)) {
      this.pause();
    }
  }

  play() {
    this.slider.setAttribute('aria-live', 'off');
    clearInterval(this.autoplay);
    this.autoplay = setInterval(this.autoRotateSlides.bind(this), this.autoplaySpeed);
  }

  pause() {
    this.slider.setAttribute('aria-live', 'polite');
    clearInterval(this.autoplay);
  }

  togglePlayButtonState(pauseAutoplay) {
    if (pauseAutoplay) {
      this.sliderAutoplayButton.classList.add('slideshow__autoplay--paused');
      this.sliderAutoplayButton.setAttribute('aria-label', window.accessibilityStrings.playSlideshow);
    } else {
      this.sliderAutoplayButton.classList.remove('slideshow__autoplay--paused');
      this.sliderAutoplayButton.setAttribute('aria-label', window.accessibilityStrings.pauseSlideshow);
    }
  }

  autoRotateSlides() {
    const slideScrollPosition =
      this.currentPage === this.sliderItems.length ? 0 : this.slider.scrollLeft + this.sliderItemOffset;

    this.setSlidePosition(slideScrollPosition);
    this.applyAnimationToAnnouncementBar();
  }

  setSlideVisibility(event) {
    this.sliderItemsToShow.forEach((item, index) => {
      const linkElements = item.querySelectorAll('a');
      if (index === this.currentPage - 1) {
        if (linkElements.length)
          linkElements.forEach((button) => {
            button.removeAttribute('tabindex');
          });
        item.setAttribute('aria-hidden', 'false');
        item.removeAttribute('tabindex');
      } else {
        if (linkElements.length)
          linkElements.forEach((button) => {
            button.setAttribute('tabindex', '-1');
          });
        item.setAttribute('aria-hidden', 'true');
        item.setAttribute('tabindex', '-1');
      }
    });
    this.wasClicked = false;
  }

  applyAnimationToAnnouncementBar(button = 'next') {
    if (!this.announcementBarSlider) return;

    const itemsCount = this.sliderItems.length;
    const increment = button === 'next' ? 1 : -1;

    const currentIndex = this.currentPage - 1;
    let nextIndex = (currentIndex + increment) % itemsCount;
    nextIndex = nextIndex === -1 ? itemsCount - 1 : nextIndex;

    const nextSlide = this.sliderItems[nextIndex];
    const currentSlide = this.sliderItems[currentIndex];

    const animationClassIn = 'announcement-bar-slider--fade-in';
    const animationClassOut = 'announcement-bar-slider--fade-out';

    const isFirstSlide = currentIndex === 0;
    const isLastSlide = currentIndex === itemsCount - 1;

    const shouldMoveNext = (button === 'next' && !isLastSlide) || (button === 'previous' && isFirstSlide);
    const direction = shouldMoveNext ? 'next' : 'previous';

    currentSlide.classList.add(`${animationClassOut}-${direction}`);
    nextSlide.classList.add(`${animationClassIn}-${direction}`);

    setTimeout(() => {
      currentSlide.classList.remove(`${animationClassOut}-${direction}`);
      nextSlide.classList.remove(`${animationClassIn}-${direction}`);
    }, this.announcerBarAnimationDelay * 2);
  }

  linkToSlide(event) {
    event.preventDefault();
    const slideScrollPosition =
      this.slider.scrollLeft +
      this.sliderFirstItemNode.clientWidth *
        (this.sliderControlLinksArray.indexOf(event.currentTarget) + 1 - this.currentPage);
    this.slider.scrollTo({
      left: slideScrollPosition,
    });
  }
}

customElements.define('slideshow-component', SlideshowComponent);

class VariantSelects extends HTMLElement {
  constructor() {
    super();
  }

  connectedCallback() {
    this.addEventListener('change', (event) => {
      const target = this.getInputForEventTarget(event.target);
      this.updateSelectionMetadata(event);

      this.dispatchProductSelectEvent();

      publish(PUB_SUB_EVENTS.optionValueSelectionChange, {
        data: {
          event,
          target,
          selectedOptionValues: this.selectedOptionValues,
        },
      });
    });
  }

  getAllSelectedOptions() {
    const options = [];
    this.querySelectorAll('fieldset, .product-form__input--dropdown').forEach((group) => {
      const checked = group.querySelector('input:checked') || group.querySelector('select option[selected]');
      if (checked) {
        options.push({ name: checked.dataset.optionName || '', value: checked.value });
      }
    });
    return options;
  }

  dispatchProductSelectEvent() {
    const { ProductSelectEvent } = window.StandardEvents || {};
    if (!ProductSelectEvent) return;

    const deferred = ProductSelectEvent.createPromise();
    this.pendingSelectPromise = deferred;

    this.dispatchEvent(
      new ProductSelectEvent({
        product: {
          id: this.dataset.productId,
          title: this.dataset.productTitle,
          handle: this.dataset.productHandle,
        },
        selectedOptions: this.getAllSelectedOptions(),
        promise: deferred.promise,
      })
    );
  }

  takePendingSelectPromise() {
    const deferred = this.pendingSelectPromise;
    this.pendingSelectPromise = null;
    return deferred;
  }

  resolvePendingSelectPromise(variant, sourceVariantSelects = this) {
    const deferred = this.takePendingSelectPromise();
    if (!deferred) return;

    if (variant) {
      deferred.resolve({
        variant: {
          id: variant.id,
          title: variant.title,
          availableForSale: variant.available,
          price: {
            amount: sourceVariantSelects?.dataset.selectedPriceAmount,
            currencyCode: sourceVariantSelects?.dataset.currencyCode,
          },
          selectedOptions: this.getAllSelectedOptions(),
        },
      });
    } else {
      deferred.resolve({ variant: null });
    }
  }

  rejectPendingSelectPromise(error) {
    this.takePendingSelectPromise()?.reject(error);
  }

  updateSelectionMetadata({ target }) {
    const { value, tagName } = target;

    if (tagName === 'SELECT' && target.selectedOptions.length) {
      Array.from(target.options)
        .find((option) => option.getAttribute('selected'))
        .removeAttribute('selected');
      target.selectedOptions[0].setAttribute('selected', 'selected');

      const swatchValue = target.selectedOptions[0].dataset.optionSwatchValue;
      const selectedDropdownSwatchValue = target
        .closest('.product-form__input')
        .querySelector('[data-selected-value] > .swatch');
      if (!selectedDropdownSwatchValue) return;
      if (swatchValue) {
        selectedDropdownSwatchValue.style.setProperty('--swatch--background', swatchValue);
        selectedDropdownSwatchValue.classList.remove('swatch--unavailable');
      } else {
        selectedDropdownSwatchValue.style.setProperty('--swatch--background', 'unset');
        selectedDropdownSwatchValue.classList.add('swatch--unavailable');
      }

      selectedDropdownSwatchValue.style.setProperty(
        '--swatch-focal-point',
        target.selectedOptions[0].dataset.optionSwatchFocalPoint || 'unset'
      );
    } else if (tagName === 'INPUT' && target.type === 'radio') {
      const selectedSwatchValue = target.closest(`.product-form__input`).querySelector('[data-selected-value]');
      if (selectedSwatchValue) selectedSwatchValue.innerHTML = value;
    }
  }

  getInputForEventTarget(target) {
    return target.tagName === 'SELECT' ? target.selectedOptions[0] : target;
  }

  get selectedOptionValues() {
    return Array.from(this.querySelectorAll('select option[selected], fieldset input:checked')).map(
      ({ dataset }) => dataset.optionValueId
    );
  }
}

customElements.define('variant-selects', VariantSelects);

class ProductRecommendations extends HTMLElement {
  observer = undefined;

  constructor() {
    super();
  }

  connectedCallback() {
    this.initializeRecommendations(this.dataset.productId);
  }

  initializeRecommendations(productId) {
    this.observer?.unobserve(this);
    this.observer = new IntersectionObserver(
      (entries, observer) => {
        if (!entries[0].isIntersecting) return;
        observer.unobserve(this);
        this.loadRecommendations(productId);
      },
      { rootMargin: '0px 0px 400px 0px' }
    );
    this.observer.observe(this);
  }

  loadRecommendations(productId) {
    fetch(`${this.dataset.url}&product_id=${productId}&section_id=${this.dataset.sectionId}`)
      .then((response) => response.text())
      .then((text) => {
        const html = document.createElement('div');
        html.innerHTML = text;
        const recommendations = html.querySelector('product-recommendations');

        if (recommendations?.innerHTML.trim().length) {
          this.innerHTML = recommendations.innerHTML;
        }

        if (!this.querySelector('slideshow-component') && this.classList.contains('complementary-products')) {
          this.remove();
        }

        if (html.querySelector('.grid__item')) {
          this.classList.add('product-recommendations--loaded');
        }
      })
      .catch((e) => {
        console.error(e);
      });
  }
}

customElements.define('product-recommendations', ProductRecommendations);

class BulkAdd extends HTMLElement {
  static ASYNC_REQUEST_DELAY = 250;

  constructor() {
    super();
    this.queue = [];
    this.setRequestStarted(false);
    this.ids = [];
  }

  startQueue(id, quantity) {
    this.queue.push({ id, quantity });

    const interval = setInterval(() => {
      if (this.queue.length > 0) {
        if (!this.requestStarted) {
          this.sendRequest(this.queue);
        }
      } else {
        clearInterval(interval);
      }
    }, BulkAdd.ASYNC_REQUEST_DELAY);
  }

  sendRequest(queue) {
    this.setRequestStarted(true);
    const items = {};

    queue.forEach((queueItem) => {
      items[parseInt(queueItem.id)] = queueItem.quantity;
    });
    this.queue = this.queue.filter((queueElement) => !queue.includes(queueElement));

    this.updateMultipleQty(items);
  }

  setRequestStarted(requestStarted) {
    this._requestStarted = requestStarted;
  }

  get requestStarted() {
    return this._requestStarted;
  }

  getCartQuantityForLine(id) {
    const input = this.querySelector(`#Quantity-${id}`);
    return parseInt(input?.dataset.cartQuantity || input?.getAttribute('value') || '0', 10) || 0;
  }

  startCartLinesUpdate(items) {
    const { CartLinesUpdateEvent } = window.StandardEvents || {};
    if (!CartLinesUpdateEvent) return;

    const linesByAction = Object.entries(items).reduce((groups, [variantId, quantity]) => {
      const nextQuantity = parseInt(quantity, 10);
      const currentQuantity = this.getCartQuantityForLine(variantId);

      if (Number.isNaN(nextQuantity) || currentQuantity === nextQuantity) return groups;

      const action = currentQuantity === 0 ? 'add' : nextQuantity === 0 ? 'remove' : 'update';
      let line;
      if (action === 'add') {
        line = { merchandiseId: variantId, quantity: nextQuantity };
      } else {
        const lineKey = this.querySelector(`[data-quantity-variant-id="${variantId}"]`)?.dataset.quantityLineKey;
        // No AJAX line key on the row — likely cached HTML rendered before this
        // attribute landed. Skip rather than emit an event with id: ''.
        if (!lineKey) return groups;
        line = { id: lineKey, quantity: nextQuantity };
      }

      if (!groups[action]) groups[action] = [];
      groups[action].push(line);
      return groups;
    }, {});

    const deferreds = Object.entries(linesByAction).map(([action, lines]) => {
      const deferred = CartLinesUpdateEvent.createPromise();
      this.dispatchEvent(
        new CartLinesUpdateEvent({
          action,
          context: 'product',
          lines,
          promise: deferred.promise,
        })
      );
      return deferred;
    });

    return {
      resolve: (parsedState) => {
        const payload = { cart: CartLinesUpdateEvent.createCartFromAjaxResponse(parsedState) };
        deferreds.forEach((deferred) => deferred.resolve(payload));
      },
      reject: (error) => {
        deferreds.forEach((deferred) => deferred.reject(error));
      },
    };
  }

  dispatchCartErrorEvent(message, code) {
    const { CartErrorEvent } = window.StandardEvents || {};
    if (!CartErrorEvent) return;

    this.dispatchEvent(new CartErrorEvent({ error: message, code }));
  }

  resetQuantityInput(id) {
    const input = this.querySelector(`#Quantity-${id}`);
    input.value = input.getAttribute('value');
    this.isEnterPressed = false;
  }

  setValidity(event, index, message) {
    event.target.setCustomValidity(message);
    event.target.reportValidity();
    this.resetQuantityInput(index);
    event.target.select();
  }

  validateQuantity(event) {
    const inputValue = parseInt(event.target.value);
    const index = event.target.dataset.index;

    if (inputValue < event.target.dataset.min) {
      this.setValidity(event, index, window.quickOrderListStrings.min_error.replace('[min]', event.target.dataset.min));
    } else if (inputValue > parseInt(event.target.max)) {
      this.setValidity(event, index, window.quickOrderListStrings.max_error.replace('[max]', event.target.max));
    } else if (inputValue % parseInt(event.target.step) != 0) {
      this.setValidity(event, index, window.quickOrderListStrings.step_error.replace('[step]', event.target.step));
    } else {
      event.target.setCustomValidity('');
      event.target.reportValidity();
      event.target.setAttribute('value', inputValue);
      this.startQueue(index, inputValue);
    }
  }

  getSectionInnerHTML(html, selector) {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }
}

if (!customElements.get('bulk-add')) {
  customElements.define('bulk-add', BulkAdd);
}

class CartPerformance {
  static #metric_prefix = "cart-performance"

  static createStartingMarker(benchmarkName) {
    const metricName = `${CartPerformance.#metric_prefix}:${benchmarkName}`
    return performance.mark(`${metricName}:start`);
  }

  static measureFromEvent(benchmarkName, event) {
    const metricName = `${CartPerformance.#metric_prefix}:${benchmarkName}`
    const startMarker = performance.mark(`${metricName}:start`, {
      startTime: event.timeStamp
    });

    const endMarker = performance.mark(`${metricName}:end`);

    performance.measure(
      metricName,
      `${metricName}:start`,
      `${metricName}:end`
    );
  }

  static measureFromMarker(benchmarkName, startMarker) {
    const metricName = `${CartPerformance.#metric_prefix}:${benchmarkName}`
    const endMarker = performance.mark(`${metricName}:end`);

    performance.measure(
      metricName,
      startMarker.name,
      `${metricName}:end`
    );
  }

  static measure(benchmarkName, callback) {
    const metricName = `${CartPerformance.#metric_prefix}:${benchmarkName}`
    const startMarker = performance.mark(`${metricName}:start`);

    callback();

    const endMarker = performance.mark(`${metricName}:end`);

    performance.measure(
      metricName,
      `${metricName}:start`,
      `${metricName}:end`
    );
  }
}


// One accepted selection and one cart operation for PDP, sticky bar and scanner.
const spaceArmoryOpenOverlay = 'dialog[open], modal-dialog[open], quick-add-modal[open], product-modal[open]';
class SpaceArmoryPurchaseController {
  constructor(info, restored) {
    this.info = info;
    this.root = info.querySelector('face-fit-scanner');
    this.names = JSON.parse(this.root.dataset.productOptions || '[]');
    this.stickyPrices = JSON.parse(info.querySelector('[data-sticky-prices]')?.textContent || 'null');
    this.variants = JSON.parse(this.root.querySelector('[data-face-fit-variants]').textContent);
    this.bundleSizes = this.root.dataset.bundleSizes === 'true';
    // PDP v2: Add to Cart stays visible; size is still required before adding.
    this.v2 = this.root.dataset.pdpV2 === 'true';
    this.sizeIndices = this.names.flatMap((name, index) => (/^(size|fit|beden|size fit)$/i.test(name.trim()) || (this.bundleSizes && / (size|fit|beden)$/i.test(name.replace(/[()]/g, ' ').trim()))) ? [index] : []);
    this.sizeIndex = restored?.sizeIndex ?? this.sizeIndices[0];
    this.abort = new AbortController();
    this.error = '';
    this.pending = restored?.pending || null;
    this.hasCompletedScan = restored?.hasCompletedScan === true;
    this.savedMeasurement = restored?.savedMeasurement || null;
    this.profileKey = restored?.profileKey || '';
    this.hasFreshScan = restored?.hasFreshScan || false;
    this.freshMeasurement = restored?.freshMeasurement || null;
    this.profileCustomer = restored?.profileCustomer || null;
    this.revision = 0;
    this.values = this.readValues();
    const urlVariant = this.variants.find(v => String(v.id) === new URL(location.href).searchParams.get('variant'));
    const restoredControl = this.groups()[this.sizeIndex]?.querySelector('input:checked');
    const restoredSelect = this.groups()[this.sizeIndex]?.querySelector('select');
    // A server-rendered first variant is not customer consent. Browser restoration is.
    const browserRestored = (restoredControl && !restoredControl.defaultChecked) ||
      (restoredSelect && restoredSelect.selectedOptions[0] && !restoredSelect.selectedOptions[0].defaultSelected);
    if (restored) this.values = [...restored.values];
    else if (urlVariant) this.values = [...urlVariant.options];
    else if (!browserRestored) this.sizeIndices.forEach(index => { this.values[index] = ''; });
    if (this.bundleSizes) {
      const target = this.root.querySelector('[data-bundle-size-target]');
      if (target) target.value = String(this.sizeIndex);
    }
    info.faceFitPurchase = this;
    info.classList.add('face-fit-purchase-ready');
    this.writeControls();
    this.sync();
    if (this.pending) {
      const inFlight = this.pending;
      const settle = () => { if (this.pending === inFlight) { this.pending = null; this.sync(); } };
      inFlight.then(settle, error => { this.error = error.message; settle(); });
    }
    const signal = this.abort.signal;
    info.addEventListener('change', event => {
      if (event.target.matches('[data-bundle-size-target]')) {
        if (this.pending) return;
        this.sizeIndex = Number(event.target.value);
        this.savedMeasurement = null;
        this.hasFreshScan = false;
        this.clearProfileProperties();
        this.root.dispatchEvent(new CustomEvent('space-armory:clear-profile'));
        this.sync();
        return;
      }
      this.onChange(event);
    }, {capture: true, signal});
    info.addEventListener('click', event => {
      const target = event.target.closest('[data-purchase-primary], [data-purchase-scan], [data-purchase-manual], [data-purchase-change]');
      if (!target) return;
      event.preventDefault();
      if (target.matches('[data-purchase-primary]')) this.primaryAction(event);
      else if (target.matches('[data-purchase-scan]')) this.openScanner();
      else if (target.matches('[data-purchase-manual]')) this.openManual(target);
      else this.focusSizes();
    }, {signal});
    const profile = window.SpaceArmoryFitAccount;
    profile?.addEventListener('change', () => this.acceptProfile(profile.state), {signal});
    if (profile) this.acceptProfile(profile.state);
    info.addEventListener('space-armory:scanner-visibility', () => this.updateSticky(), {signal});
    info.addEventListener('space-armory:scan-complete', event => {
      if (event.target !== this.root || !window.SpaceArmoryFitRules?.valid(event.detail?.measurement)) return;
      this.hasFreshScan = true;
      this.freshMeasurement = event.detail?.measurement;
      if (this.bundleSizes && this.form) {
        let target = this.form.querySelector('input[name="properties[Measurement for]"]');
        if (!target) { target = document.createElement('input'); target.type = 'hidden'; target.name = 'properties[Measurement for]'; this.form.append(target); }
        target.value = this.names[this.sizeIndex];
      }
      this.savedMeasurement = null;
      this.form?.querySelector('input[name="properties[_Face Fit Action]"]')?.remove();
      this.sync();
    }, {signal});
    window.addEventListener('scroll', () => this.updateSticky(), {passive: true, signal});
    window.addEventListener('resize', () => this.updateSticky(), {passive: true, signal});
    window.addEventListener('pageshow', () => {
      this.values = this.readValues();
      this.sync();
    }, {signal});
    this.intersection = typeof IntersectionObserver === 'function' ? new IntersectionObserver(() => this.updateSticky()) : null;
    this.intersection?.observe(this.stickyBoundary());
    this.updateSticky();
  }
  acceptProfile(state) {
    if (this.bundleSizes && this.sizeIndices.length > 1) return;
    if (state.status === 'loading' || this.pending) return;
    const identityChanged = this.profileCustomer && this.profileCustomer !== state.customer;
    const key = `${state.customer || ''}:${state.revision}:${state.status}`;
    if (key === this.profileKey) return;
    this.profileKey = key;
    this.profileCustomer = state.customer;
    if (identityChanged) { this.hasFreshScan = false; this.freshMeasurement = null; }
    if (!state.measurement || identityChanged) {
      if (this.savedMeasurement || identityChanged) {
        this.savedMeasurement = null; this.hasCompletedScan = false;
        this.values[this.sizeIndex] = ''; this.writeControls();
        this.clearProfileProperties();
        this.root.dispatchEvent(new CustomEvent('space-armory:clear-profile'));
      }
      this.sync();
      if (!state.measurement) return;
    }
    if (!window.SpaceArmoryFitRules?.valid(state.measurement)) return;
    if (this.hasFreshScan) {
      if(state.measurement.measuredAt!==this.freshMeasurement?.measuredAt || state.measurement.cm!==this.freshMeasurement?.cm)return;
      this.hasFreshScan=false;
    }
    this.savedMeasurement = {...state.measurement, customer:state.customer, revision:state.revision};
    this.hasCompletedScan = true;
    const size = window.SpaceArmoryFitRules.sizeFor(state.measurement.cm);
    const aliases = {S:['s','small'],M:['m','medium'],L:['l','large'],XL:['xl','x-large','extra large'],XXL:['xxl','2xl','xx-large','extra extra large']};
    const group = this.groups()[this.sizeIndex];
    const control = Array.from(group?.querySelectorAll('input[type="radio"], select option') || []).find(el => aliases[size]?.includes(el.value.trim().toLowerCase()));
    this.values[this.sizeIndex] = control?.value || '';
    this.error = control ? '' : 'Your saved size is not offered for this product.';
    this.writeControls(); this.sync();
    if(control && this.resolve()?.available){
      this.applyingProfile=true;
      try{(control.tagName==='OPTION'?control.parentElement:control).dispatchEvent(new Event('change',{bubbles:true}));}
      finally{this.applyingProfile=false;}
    }
  }
  clearProfileProperties() {
    this.form?.querySelectorAll('input[name^="properties["]').forEach(input => {
      if (['properties[Measurement for]', 'properties[Measured Size]', 'properties[_Face Fit Profile Revision]', 'properties[_Face Fit Profile Customer]', 'properties[_Face Fit Source]', 'properties[_Face Fit Measurement]', 'properties[_Face Fit Action]', 'properties[_Face Fit Device]', ...(window.SpaceArmoryFitRules?.scanStatsKeys || []).map(key => `properties[${key}]`)].includes(input.name)) input.remove();
    });
  }
  attachProfileProperties() {
    if (!this.savedMeasurement) return;
    // A save completing after a fresh scan must not erase its purchase action.
    const action = this.form?.querySelector('input[name="properties[_Face Fit Action]"]')?.value;
    const sameResult = this.freshMeasurement?.cm === this.savedMeasurement.cm &&
      this.freshMeasurement?.measuredAt === this.savedMeasurement.measuredAt;
    this.clearProfileProperties();
    const properties = {
      'Measured Size': window.SpaceArmoryFitRules.text(this.savedMeasurement),
      '_Face Fit Profile Revision': String(this.savedMeasurement.revision),
      '_Face Fit Source': `saved_profile | ${window.SpaceArmoryFitRules.version}`,
      '_Face Fit Action': sameResult && ['Only Apply Size', 'Apply Size & Add to Cart', 'Add to Cart from product page after measurement'].includes(action) ? action : 'Use saved measurement',
    };
    // Account records do not store device data. Preserve it only when this page
    // just measured the very same result; never label an old scan with today's device.
    const device = sameResult && window.SpaceArmoryFitDevice?.text(this.freshMeasurement?.device);
    if (device) properties['_Face Fit Device'] = device;
    Object.assign(properties, window.SpaceArmoryFitRules.scanStatsProperties?.(
      this.savedMeasurement.scanStats || (sameResult ? this.freshMeasurement?.scanStats : null)));
    Object.entries(properties).forEach(([name,value]) => {
      const input=document.createElement('input'); input.type='hidden'; input.name=`properties[${name}]`; input.value=value; this.form.append(input);
    });
  }
  groups() { return Array.from(this.info.querySelector('variant-selects')?.querySelectorAll('.product-form__input') || []); }
  readValues() {
    return this.groups().map(group => (group.querySelector('input:checked') || group.querySelector('select'))?.value || '');
  }
  get size() { return this.values[this.sizeIndex] || ''; }
  get hasRequiredSizes() { return this.sizeIndices.every(index => !!this.values[index]); }
  get formElement() { return this.info.querySelector('product-form'); }
  get form() { return this.formElement?.querySelector('form'); }
  resolve(values = this.values) {
    return this.variants.find(v => v.options.length === values.length && v.options.every((value, i) => value === values[i])) || null;
  }
  writeControls() {
    this.groups().forEach((group, index) => {
      const value = this.values[index] || '';
      group.querySelectorAll('input[type="radio"]').forEach(input => { input.checked = input.value === value; });
      const select = group.querySelector('select');
      if (select) {
        if (this.sizeIndices.includes(index) && !select.querySelector('[data-size-placeholder]')) {
          const placeholder = document.createElement('option');
          placeholder.value = ''; placeholder.textContent = 'Choose a size'; placeholder.dataset.sizePlaceholder = ''; placeholder.disabled = true;
          select.prepend(placeholder);
        }
        select.value = value;
      }
      const selectedText = group.querySelector('legend [data-selected-value]');
      if (selectedText) selectedText.textContent = value;
    });
  }
  onChange(event) {
    const group = event.target.closest('variant-selects .product-form__input');
    if (!group || !event.target.matches('input[type="radio"], select')) return;
    if (this.pending) {
      event.stopImmediatePropagation(); this.writeControls(); return;
    }
    if (this.bundleSizes && this.sizeIndices.includes(this.groups().indexOf(group))) {
      this.sizeIndex = this.groups().indexOf(group);
      const target = this.root.querySelector('[data-bundle-size-target]');
      if (target) target.value = String(this.sizeIndex);
    }
    if (group === this.groups()[this.sizeIndex] && !this.applyingProfile) {
      this.form?.querySelector('input[name="properties[_Face Fit Action]"]')?.remove();
    }
    if (group === this.groups()[this.sizeIndex] && this.savedMeasurement && !this.applyingProfile) {
      this.savedMeasurement = null; this.clearProfileProperties();
    }
    this.values = this.readValues();
    this.revision++;
    this.error = '';
    this.sync();
  }
  reconcile(variant) {
    // Native section HTML may default an unchosen option. Keep the accepted choices.
    const local = this.resolve();
    if (local && variant && String(local.id) === String(variant.id)) local.available = variant.available;
    this.writeControls();
    this.sync();
    if (!this.hasRequiredSizes) { const url = new URL(location.href); url.searchParams.delete('variant'); history.replaceState(history.state, '', url); }
  }
  sync() {
    this.variant = this.resolve();
    const valid = this.hasRequiredSizes && !!this.variant;
    const available = valid && this.variant.available;
    const busy = !!this.pending;
    this.info.querySelectorAll('form input[name="id"]').forEach(input => {
      input.value = valid ? String(this.variant.id) : '';
      input.disabled = !valid;
    });
    const stickyPrice = this.stickyPrices?.variants?.[this.variant?.id] || this.stickyPrices?.default;
    const priceText = this.info.querySelector('[data-sticky-price]');
    if (priceText && stickyPrice) priceText.textContent = stickyPrice.display;
    const label = busy ? 'Adding…' : !this.hasRequiredSizes ? 'Add to Cart' :
      !valid ? `Unavailable — ${this.size}` : !available ? `Sold Out — ${this.size}` : (this.bundleSizes && this.sizeIndices.length > 1 ? 'Add to Cart' : `Add to Cart — ${this.size}`);
    const v2Ready = this.v2 && !busy && valid && available && !(this.bundleSizes && this.sizeIndices.length > 1);
    this.info.querySelectorAll('[data-purchase-primary]').forEach(button => {
      const sticky = !!button.closest('.sticky_add_btn_wrapper');
      let buttonLabel = !this.hasRequiredSizes && !busy && sticky ? (button.dataset.selectSizeLabel || 'SELECT A SIZE') : label;
      if (v2Ready) buttonLabel = sticky ? `Add to Cart · ${this.size}` : `Add to Cart · ${this.size} — ${stickyPrice?.amount || ''}`.replace(/ — $/, '');
      const text = button.querySelector('[data-entry-title]');
      if (text) {
        text.textContent = buttonLabel;
        if (this.savedMeasurement && !sticky && !this.v2) {
          const caption=document.createElement('small');caption.className='face-fit__saved-caption';
          caption.textContent=`Saved measurement: ${this.savedMeasurement.cm.toFixed(1)} cm`;
          text.append(caption);
        }
      }
      else button.textContent = buttonLabel;
      button.disabled = busy || (this.hasRequiredSizes && !available);
      button.setAttribute('aria-busy', String(busy));
      button.dataset.mode = this.hasRequiredSizes ? 'cart' : 'select';
    });
    const group = this.groups()[this.sizeIndex];
    this.sizeIndices.forEach(index => this.groups()[index]?.classList.add('face-fit-size-options'));
    const row = this.root.querySelector('[data-purchase-row]');
    if (row) row.dataset.hasSize = String(this.hasRequiredSizes);
    const scanOpen = this.root.querySelector('[data-purchase-open]');
    if (scanOpen) { scanOpen.hidden = this.hasRequiredSizes; scanOpen.disabled = busy; }
    const cart = this.root.querySelector('[data-purchase-cart]');
    if (cart) cart.hidden = !this.v2 && !this.hasRequiredSizes;
    const compactManual = this.root.querySelector('[data-purchase-manual]');
    if (compactManual) { compactManual.hidden = !this.v2 && !this.hasRequiredSizes; compactManual.disabled = busy; }
    const manualGuide = this.info.querySelector('manual-size-guide');
    if (manualGuide) {
      manualGuide.classList.toggle('face-fit__manual-collapsed', this.hasRequiredSizes);
      const manualOpen = manualGuide.querySelector('[data-manual-guide-open]');
      if (manualOpen) manualOpen.hidden = this.hasRequiredSizes;
    }
    const rescan = this.root.querySelector('[data-purchase-rescan]');
    if (rescan) {
      rescan.hidden = !this.hasRequiredSizes;
      rescan.disabled = busy;
      const text = rescan.querySelector('.face-fit__entry-title') || rescan;
      text.textContent = this.hasCompletedScan ? 'Measure My Face Again' : 'Measure My Face';
    }
    const status = this.root.querySelector('[data-purchase-status]');
    const message = this.error || (this.hasRequiredSizes && !available ? (!valid ? 'This combination is unavailable. Choose another size or option.' : `Size ${this.size} is sold out. Choose another size or option.`) : '');
    if (status) { status.textContent = message; status.hidden = !message; }
    const stickyStatus = this.info.querySelector('[data-sticky-status]');
    if (stickyStatus) { stickyStatus.textContent = message; stickyStatus.hidden = !message; }
    // The native submit remains in its original form; visible controls are proxies.
    const native = this.formElement?.querySelector('[type="submit"]');
    if (native) { native.disabled = !available || busy; native.setAttribute('aria-disabled', String(!available || busy)); }
    this.info.querySelectorAll('variant-selects .product-form__input').forEach(group => { group.inert = busy; group.setAttribute('aria-disabled', String(busy)); });
    if (this.v2) this.syncV2();
    this.updateSticky();
  }
  // Mirrors the accepted size and measurement into the PDP v2 size row, finder card and sticky bar.
  syncV2() {
    const rules = window.SpaceArmoryFitRules;
    const measurement = this.savedMeasurement || (this.hasCompletedScan && rules?.valid(this.freshMeasurement) ? this.freshMeasurement : null);
    const fitSize = measurement && rules ? rules.sizeFor(measurement.cm) : '';
    const aliases = {S:['s','small'],M:['m','medium'],L:['l','large'],XL:['xl','x-large','extra large'],XXL:['xxl','2xl','xx-large','extra extra large']};
    const group = this.groups()[this.sizeIndex];
    const labels = Array.from(group?.querySelectorAll('label[for]') || []);
    group?.querySelectorAll('input[type="radio"]').forEach(input => {
      const label = input.id ? labels.find(candidate => candidate.htmlFor === input.id) : null;
      if (label) label.toggleAttribute('data-sa-fit', !!fitSize && (aliases[fitSize] || []).includes(input.value.trim().toLowerCase()));
    });
    const range = group?.querySelector('input:checked + label [data-sa-v2-range]')?.textContent || '';
    const value = this.info.querySelector('[data-sa-v2-size-value]');
    if (value) value.textContent = this.size ? [this.size, range].filter(Boolean).join(' · ') : 'Select Your Size';
    const saved = this.root.querySelector('[data-sa-v2-finder-saved]');
    const fallback = this.root.querySelector('[data-sa-v2-finder-default]');
    if (saved && fallback) {
      saved.hidden = !measurement || !fitSize;
      fallback.hidden = !saved.hidden;
      if (measurement && fitSize) {
        saved.querySelector('[data-sa-v2-saved-title]').textContent = `Your Size: ${fitSize} · ${measurement.cm.toFixed(1)} cm`;
        saved.querySelector('[data-sa-v2-saved-caption]').textContent = this.savedMeasurement ? 'From Your Face Scan · Saved to Your Account' : 'From Your Face Scan';
      }
    }
    const stickySize = this.info.querySelector('[data-sa-v2-sticky-size]');
    if (stickySize) { stickySize.textContent = this.size ? `Size ${this.size} · ` : ''; stickySize.hidden = !this.size; }
    const compare = this.info.querySelector('[data-sa-v2-sticky-compare]');
    const compareText = this.stickyPrices?.variants?.[this.variant?.id]?.compare;
    if (compare && compareText) compare.textContent = compareText;
    if (this.hasRequiredSizes) this.info.classList.remove('sa-v2-size-required');
  }
  openScanner() { this.root.dispatchEvent(new CustomEvent('space-armory:open-scanner')); }
  openManual(trigger) { this.info.querySelector('manual-size-guide')?.open?.(trigger); }
  primaryAction(event) {
    if (this.pending) return;
    if (!this.hasRequiredSizes) {
      // The sticky "Select Size" only scrolls to the sizes; the message is for the main Add to Cart.
      if (this.v2 && !event?.target?.closest?.('.sticky_add_btn_wrapper')) {
        this.error = 'Please choose your size to continue.';
        this.info.classList.remove('sa-v2-size-required');
        void this.info.offsetWidth;
        this.info.classList.add('sa-v2-size-required');
        this.sync();
      }
      this.focusSizes(true, event);
      return;
    }
    this.addToCart(event).catch(() => {}); // Errors are rendered by the central operation.
  }
  focusSizes(animate = false, event) {
    if (this.bundleSizes) {
      this.sizeIndex = this.sizeIndices.find(index => !this.values[index]) ?? this.sizeIndex;
      const target = this.root.querySelector('[data-bundle-size-target]');
      if (target) target.value = String(this.sizeIndex);
    }
    const group = this.groups()[this.sizeIndex];
    group?.scrollIntoView({behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth', block: 'center'});
    if (animate && group) {
      const pointer = event?.detail > 0 || Boolean(event?.pointerType) || document.documentElement.dataset.saInput === 'pointer';
      if (pointer) {
        const active = document.activeElement;
        if (group.contains(active) || (this.info.contains(active) && active.matches('[data-purchase-primary]'))) active.blur();
      } else {
        group.setAttribute('tabindex', '-1');
        group.focus({preventScroll: true});
      }
    } else {
      group?.querySelector('input:checked, select, input[type="radio"]')?.focus({preventScroll: true});
    }
    if (animate && group) {
      clearTimeout(this.sizeAttentionTimer);
      group.classList.remove('face-fit-size-attention');
      void group.offsetWidth;
      group.classList.add('face-fit-size-attention');
      this.sizeAttentionTimer = setTimeout(() => group.classList.remove('face-fit-size-attention'), 480);
    }
  }
  applySize(size) {
    if (this.pending) throw new Error('cart_busy');
    const aliases = {S:['s','small'],M:['m','medium'],L:['l','large'],XL:['xl','x-large','extra large'],XXL:['xxl','2xl','xx-large','extra extra large']};
    const accepted = aliases[size] || [String(size).toLowerCase()];
    const group = this.groups()[this.sizeIndex];
    const control = Array.from(group?.querySelectorAll('input[type="radio"], select option') || []).find(el => accepted.includes(el.value.trim().toLowerCase()));
    if (!control) throw new Error('variant_missing');
    const values = [...this.values]; values[this.sizeIndex] = control.value;
    const variant = this.resolve(values) || (this.bundleSizes ? this.variants.find(v => v.available && v.options.every((value, index) => !values[index] || value === values[index])) : null);
    if (!variant) throw new Error('variant_missing');
    if (!variant.available) throw new Error('variant_unavailable');
    if (window.SpaceArmoryFitRules?.valid(this.freshMeasurement) && window.SpaceArmoryFitRules.sizeFor(this.freshMeasurement.cm) === size) {
      this.hasCompletedScan = true;
    }
    this.values = values; this.error = ''; this.revision++;
    this.writeControls(); this.sync();
    (control.tagName === 'OPTION' ? control.parentElement : control).dispatchEvent(new Event('change', {bubbles: true}));
    this.sync();
    return {variant, form: this.form};
  }
  addToCart(event, beforeCartRender) {
    if (this.pending) return this.pending;
    this.variant = this.resolve();
    if (!this.hasRequiredSizes || !this.variant || !this.variant.available) {
      this.sync();
      return Promise.reject(new Error(this.variant ? 'variant_unavailable' : 'variant_missing'));
    }
    if (!this.form?.reportValidity()) return Promise.reject(new Error('Please check the product options and quantity.'));
    this.error = '';
    // Defer execution one microtask so every view is locked before any event/fetch.
    const saved = this.savedMeasurement;
    this.pending = Promise.resolve().then(async () => {
      if (saved) {
        const state = await window.SpaceArmoryFitAccount.refresh();
        if (state.customer !== saved.customer || state.revision !== saved.revision || !state.measurement) {
          throw new Error('Your saved measurement changed. Review your size and try again.');
        }
      }
      this.root.dispatchEvent(new CustomEvent('space-armory:prepare-cart'));
      this.attachProfileProperties();
      return this.formElement.addToCart(event, {beforeCartRender});
    }).catch(error => {
      this.error = error.message || 'Could not add to your cart. Please try again.';
      throw error;
    }).finally(() => { this.pending = null; if (window.SpaceArmoryFitAccount) this.acceptProfile(window.SpaceArmoryFitAccount.state); this.sync(); });
    this.sync();
    return this.pending;
  }
  stickyBoundary() {
    if (this.v2) return this.root.querySelector('[data-purchase-cart]');
    return (this.hasRequiredSizes ? this.root.querySelector('[data-purchase-cart]') : this.info.querySelector('[data-manual-guide-open]')) || this.root.querySelector('[data-purchase-open]');
  }
  updateSticky() {
    const bar = this.info.querySelector('.sticky_add_btn_wrapper');
    const boundary = this.stickyBoundary();
    if (!bar || !boundary) return;
    const preview = document.getElementById('PBarNextFrameWrapper');
    if (preview && preview !== this.preview && typeof ResizeObserver === 'function') {
      this.previewObserver?.disconnect();
      this.preview = preview;
      this.previewObserver = new ResizeObserver(() => this.updateSticky());
      this.previewObserver.observe(preview);
    }
    const previewRect = preview?.getBoundingClientRect();
    const previewOffset = previewRect && previewRect.bottom > 0 && previewRect.height < window.innerHeight / 2 ? previewRect.height : 0;
    bar.style.bottom = `${previewOffset}px`;
    const rect = boundary.getBoundingClientRect();
    // Show only after the entire purchase area has passed above the viewport.
    // Being below the fold at the top of the page must never reveal the bar.
    const passedPurchaseArea = rect.height > 0 && rect.bottom <= 0;
    const visible = passedPurchaseArea && !document.querySelector(spaceArmoryOpenOverlay);
    bar.classList.toggle('visible', visible);
    bar.inert = !visible;
    bar.setAttribute('aria-hidden', String(!visible));
  }
  destroy() {
    clearTimeout(this.sizeAttentionTimer);
    this.abort.abort(); this.intersection?.disconnect(); this.previewObserver?.disconnect();
    this.info.classList.remove('face-fit-purchase-ready');
    delete this.info.faceFitPurchase;
  }
}

(() => {
  const controllers = new Map();
  function mountPurchases() {
    const restored = new Map();
    controllers.forEach((controller, info) => {
      if (!info.isConnected || controller.root !== info.querySelector('face-fit-scanner')) {
        restored.set(`${info.dataset.section}:${controller.root.dataset.productId}`, {
          values: controller.values, sizeIndex: controller.sizeIndex, pending: controller.pending, hasCompletedScan: controller.hasCompletedScan, savedMeasurement: controller.savedMeasurement, profileKey: controller.profileKey, profileCustomer: controller.profileCustomer, hasFreshScan: controller.hasFreshScan, freshMeasurement: controller.freshMeasurement,
        });
        controller.destroy(); controllers.delete(info);
      }
    });
    document.querySelectorAll("product-info[id^='MainProduct']").forEach(info => {
      if (controllers.has(info) || !info.querySelector('face-fit-scanner') || !info.querySelector('product-form form')) return;
      const root = info.querySelector('face-fit-scanner');
      const controller = new SpaceArmoryPurchaseController(info, restored.get(`${info.dataset.section}:${root.dataset.productId}`));
      controllers.set(info, controller);
    });
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', mountPurchases, {once: true});
  else mountPurchases();
  document.addEventListener('shopify:section:load', mountPurchases);
  document.addEventListener('product-info:loaded', mountPurchases);
  new MutationObserver(records => {
    if (records.some(record => [...record.addedNodes, ...record.removedNodes].some(node => node.nodeType === 1 && (node.matches('product-info, face-fit-scanner') || node.querySelector('product-info, face-fit-scanner'))))) mountPurchases();
    // Theme quick-add modals live under body, outside the main product-info.
    // Observe the actual open state: modalClosed fires before open is removed.
    const overlayChanged = records.some(record => record.type === 'attributes'
      ? record.target.matches('dialog, modal-dialog, quick-add-modal, product-modal')
      : [...record.addedNodes, ...record.removedNodes].some(node => node.nodeType === 1 && (node.matches(spaceArmoryOpenOverlay) || node.querySelector(spaceArmoryOpenOverlay))));
    if (overlayChanged) controllers.forEach(controller => controller.updateSticky());
  }).observe(document.documentElement, {childList: true, subtree: true, attributes: true, attributeFilter: ['open']});
})();
